import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";

import { api } from "@/lib/api";

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

export type ReportReason =
  | "spam" | "harassment" | "offtopic" | "misinformation" | "other";

export type ReportStatus = "pending" | "resolved" | "dismissed";

export interface PostReportRow {
  id: string;
  post: number;
  reason: ReportReason;
  reason_display: string;
  note: string;
  status: ReportStatus;
  status_display: string;
  reporter: number | null;
  reporter_name: string;
  resolved_by: number | null;
  resolved_by_name: string | null;
  resolved_at: string | null;
  post_excerpt: string;
  post_author_name: string | null;
  thread_id: string;
  thread_title: string;
  created_at: string;
}

interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// ---------------------------------------------------------------------------
// Report a post (any signed-in user)
// ---------------------------------------------------------------------------

export function useReportPost() {
  return useMutation({
    mutationFn: async (input: { postId: string; reason: ReportReason; note?: string }) => {
      const { data } = await api.post<PostReportRow>(
        `/posts/${input.postId}/report/`,
        { reason: input.reason, note: input.note ?? "" },
      );
      return data;
    },
  });
}

// ---------------------------------------------------------------------------
// Moderation queue (mods only)
// ---------------------------------------------------------------------------

export function usePostReports(opts: { status?: ReportStatus | ""; page?: number } = {}) {
  return useQuery<Paginated<PostReportRow>>({
    queryKey: ["post-reports", opts],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const params = new URLSearchParams();
      if (opts.status) params.set("status", opts.status);
      if (opts.page)   params.set("page",   String(opts.page));
      const { data } = await api.get<Paginated<PostReportRow>>(
        `/post-reports/?${params.toString()}`,
      );
      return data;
    },
  });
}

export function usePendingReportCount(opts: { enabled?: boolean } = {}) {
  return useQuery<{ pending: number }>({
    queryKey: ["post-reports", "pending-count"],
    enabled: opts.enabled ?? true,
    queryFn: async () => {
      const { data } = await api.get<{ pending: number }>(
        "/post-reports/pending-count/",
      );
      return data;
    },
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

function useReportDecision(decision: "resolve" | "dismiss") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<PostReportRow>(
        `/post-reports/${id}/${decision}/`,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["post-reports"] });
      qc.invalidateQueries({ queryKey: ["posts"] });  // resolve also hid the post
    },
  });
}

export const useResolveReport = () => useReportDecision("resolve");
export const useDismissReport = () => useReportDecision("dismiss");
